import {
  ADMIN_CHAT,
  ADMIN_LOCATIONS_DETAILS,
  ADMIN_ORDER_DETAILS,
  ADMIN_USERS_DETAILS,
  Button,
  ButtonSpinner,
  CHAT_CONVERSATION_PARAM,
  DetailRow,
  Disclaimer,
  Div,
  formatDateTimeShort,
  formatter,
  H5,
  lightTheme,
  Modal,
  renderProviderName,
  Separator,
  SupportTicket,
  SupportTicketOrder,
  TextButton,
  ticketTypeLabel,
  useAuthenticatedAuthUser,
  usePermissions,
  UserPermission,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  canReplyTicket,
  canResolveTicket,
  formatTicketOpened,
  subjectAddsToType,
  ticketMessages,
} from '../helpers/supportTicketLabels';
import { useSupportTicketActions } from '../hooks/useSupportTicketActions';
import { AttachOrderModal } from './AttachOrderModal';
import { MenuPriceSection } from './MenuPriceSection';
import { InlineAction, Quote, QuoteList } from './SupportTicketModal.styled';
import { TicketReplyComposer } from './TicketReplyComposer';
import { TicketStatusPill } from './TicketStatusPill';

type Props = {
  isOpen: boolean;
  ticket: SupportTicket | null;
  onClose: () => void;
};

/** Messages quoted from the thread, one bubble each, in the chat's colours. */
const Quotes = ({ text, own }: { text: string; own?: boolean }) => (
  <QuoteList>
    {ticketMessages(text).map((message, index) => (
      // Keyed by position: this is a pure split of one string.
      <Quote key={index} $own={own}>
        {message}
      </Quote>
    ))}
  </QuoteList>
);

/** A value slot with nothing in it, in the value's own size but muted. */
const Missing = ({ children }: { children: string }) => (
  <H5 $m="0" $color={lightTheme.lightGray}>
    {children}
  </H5>
);

/**
 * The attached order on one line, the way the order picker describes its
 * rows: whatever the backend could not fill in is left out rather than
 * printed as a gap. Dollars, like every amount on a ticket and unlike the
 * cents the orders list deals in.
 */
const describeOrder = (order: SupportTicketOrder) =>
  [
    order.customerName,
    order.brandName,
    renderProviderName(order.provider),
    formatDateTimeShort(order.placedAt),
    order.total === null ? null : formatter.format(order.total),
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * One ticket, and everything support can do about it.
 *
 * Laid out like the admin's other detail modals (Income Details is the
 * model): the status first, then one DetailRow per fact with the drill-in
 * arrow at the right edge - the merchant's messages are a row like any other,
 * and their arrow opens the thread they came from - and the field for writing
 * back last. Claiming is first come first served, and only the person who
 * claimed a ticket may reply from it or resolve it - the server refuses anyone
 * else. A reply to an open ticket claims it on the way. Claim shares the
 * composer's row of buttons; Hand back is a text button on the "Claimed by"
 * row, the fact it undoes, so it does not crowd Reply and Resolve. It is open
 * to anyone on purpose, as it is how a colleague's ticket is freed when their
 * shift ends. Someone with read access alone sees no actions at all.
 */
export const SupportTicketModal = ({ isOpen, ticket, onClose }: Props) => {
  const openInNewTab = useSafeWindowOpen();
  const { can } = usePermissions();
  const viewer = useAuthenticatedAuthUser();
  const canWrite = can(UserPermission.conversationWrite);

  const [attaching, setAttaching] = useState(false);
  // Claim and Hand back spin only for themselves, not for a reply in flight.
  const [owning, setOwning] = useState<'claim' | 'release' | null>(null);

  const { busy, claim, release, reply, resolve, attachOrder } =
    useSupportTicketActions(ticket?.id ?? null);

  if (!ticket) return null;

  const order = ticket.order;
  const canResolve = canResolveTicket(ticket, viewer.id);
  const canReply = canWrite && canReplyTicket(ticket, viewer.id);
  const resolved = ticket.status === 'resolved';
  const menuIssue = ticket.type === 'menuIssue';
  const messageCount = ticketMessages(ticket.details).length;

  const changeOwner = async (action: 'claim' | 'release') => {
    setOwning(action);
    await (action === 'claim' ? claim : release)();
    setOwning(null);
  };

  /* Always a new tab: the ticket is what the agent is working, and navigating
     away closes it. */
  const open = (path: string) => openInNewTab(path, { immediate: true });

  return (
    <>
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        logo={false}
        header={`Ticket #${ticket.id}`}
        /* What kind of request it is belongs with its number: it is what the
           ticket IS, the same way the header says which one. */
        subtitle={ticketTypeLabel(ticket.type)}
        textAlign="left"
        width="560px"
      >
        <Div $flex $between $alignCenter $gap="16px">
          <TicketStatusPill status={ticket.status} />
          <Disclaimer $m="0" $right $color={lightTheme.lightGray}>
            Opened {formatTicketOpened(ticket.createdAt)}
          </Disclaimer>
        </Div>

        {/* Only when the bot's one-liner says more than the type already in
            the header does - usually it is the category again. */}
        {subjectAddsToType(ticket) && (
          <>
            <Separator $m="16px 0" />
            <DetailRow label="Subject">{ticket.subject}</DetailRow>
          </>
        )}

        {/* The merchant's own words, each message its own bubble as in the
            thread. The arrow opens that thread: reading the complaint is what
            makes somebody want the rest of the conversation. */}
        <Separator $m="16px 0" />
        <DetailRow
          label={
            messageCount === 1
              ? 'Message from the merchant'
              : 'Messages from the merchant'
          }
          onOpen={() =>
            open(
              `${ADMIN_CHAT}?scope=merchant&${CHAT_CONVERSATION_PARAM}=${ticket.conversationId}`
            )
          }
        >
          <Quotes text={ticket.details} />
        </DetailRow>

        {/* Both names open the screen they name: a ticket is usually the start
            of looking the merchant or the location up, and typing a name into
            another search is the step worth removing. */}
        <Separator $m="16px 0" />
        <DetailRow
          label="Merchant"
          onOpen={() =>
            open(ADMIN_USERS_DETAILS.replace(':id', String(ticket.merchantId)))
          }
        >
          {ticket.merchantName}
        </DetailRow>

        <Separator $m="16px 0" />
        {ticket.locationName === null ? (
          <DetailRow label="Location">
            {/* The merchant has several and did not say which. */}
            <Missing>Not specified</Missing>
          </DetailRow>
        ) : (
          <DetailRow
            label="Location"
            onOpen={
              ticket.locationId === null
                ? undefined
                : () =>
                    open(
                      ADMIN_LOCATIONS_DETAILS.replace(
                        ':id',
                        String(ticket.locationId)
                      )
                    )
            }
          >
            {ticket.locationName}
          </DetailRow>
        )}

        <Separator $m="16px 0" />
        {order ? (
          <DetailRow
            label="Order"
            hint={describeOrder(order)}
            onOpen={() =>
              open(ADMIN_ORDER_DETAILS.replace(':id', String(order.id)))
            }
          >
            {`#${order.id}`}
          </DetailRow>
        ) : (
          <DetailRow
            label="Order"
            hint={
              /* A menu issue is about the menu, not one order, so no order is
                 the normal case rather than a gap to point out. */
              menuIssue
                ? 'Menu issues are about the menu, not one order.'
                : ticket.customerName
                ? `The merchant named the customer as ${ticket.customerName}.`
                : 'The merchant has not said which order this is about.'
            }
            /* Belongs to the order, not to the row of things done WITH the
               ticket - and it is the smaller of the two decisions, so it is
               not dressed as one of them. */
            right={
              canWrite && !resolved ? (
                <InlineAction>
                  <TextButton
                    color={lightTheme.primary}
                    $underline
                    disabled={busy}
                    onClick={() => setAttaching(true)}
                  >
                    Attach order
                  </TextButton>
                </InlineAction>
              ) : undefined
            }
          >
            <Missing>{menuIssue ? 'None' : 'No order attached'}</Missing>
          </DetailRow>
        )}

        {/* Right under the order, which is what narrows its dishes. */}
        <MenuPriceSection ticket={ticket} canApprove={canReply} onOpen={open} />

        {/* Side by side: neither has an arrow, and two names read as a pair. */}
        {(ticket.claimedBy || ticket.resolvedBy) && (
          <>
            <Separator $m="16px 0" />
            <Div $flex $gap="24px" $columnSM $gapSM="16px">
              {ticket.claimedBy && (
                /* The whole width until there is a Resolved by beside it, so
                   Hand back sits at the row's right edge like Attach order. */
                <Div
                  $width={ticket.resolvedBy ? '50%' : '100%'}
                  $widthSM="100%"
                  $minWidth="0"
                >
                  <DetailRow
                    label="Claimed by"
                    hint={
                      ticket.claimedAt && formatDateTimeShort(ticket.claimedAt)
                    }
                    right={
                      canWrite && ticket.status === 'claimed' ? (
                        <Div $flex $alignCenter $gap="8px" $flexNone>
                          {owning === 'release' && (
                            /* ButtonSpinner draws a 100px box offset from its
                               anchor's right edge; -92 centres it on this
                               16px slot, just left of the words. */
                            <Div $relative $width="16px" $height="16px">
                              <ButtonSpinner
                                color={lightTheme.primary}
                                right={-92}
                              />
                            </Div>
                          )}
                          <InlineAction>
                            <TextButton
                              color={lightTheme.primary}
                              $underline
                              disabled={busy}
                              onClick={() => changeOwner('release')}
                            >
                              Hand back
                            </TextButton>
                          </InlineAction>
                        </Div>
                      ) : undefined
                    }
                  >
                    {ticket.claimedBy}
                  </DetailRow>
                </Div>
              )}
              {ticket.resolvedBy && (
                <Div $width="50%" $widthSM="100%" $minWidth="0">
                  <DetailRow
                    label="Resolved by"
                    hint={
                      ticket.resolvedAt &&
                      formatDateTimeShort(ticket.resolvedAt)
                    }
                  >
                    {ticket.resolvedBy}
                  </DetailRow>
                </Div>
              )}
            </Div>
          </>
        )}

        {/* Support's reply in the chat's own red, so the two sides of the
            exchange read the way they do in the thread. */}
        {ticket.resolution && (
          <>
            <Separator $m="16px 0" />
            <DetailRow label="Resolution sent to the merchant">
              <Quotes text={ticket.resolution} own />
            </DetailRow>
          </>
        )}

        {canReply && (
          <>
            <Separator $m="16px 0" />
            <TicketReplyComposer
              key={ticket.id}
              busy={busy}
              claimsOnReply={ticket.status === 'open'}
              canResolve={canResolve}
              leading={
                ticket.status === 'open' && (
                  <Button
                    $selected
                    $w="100%"
                    $wSM="100%"
                    $loading={owning === 'claim'}
                    disabled={busy && owning !== 'claim'}
                    onClick={() => changeOwner('claim')}
                  >
                    Claim
                  </Button>
                )
              }
              onReply={reply}
              onResolve={resolve}
            />
          </>
        )}

        {/* Somebody else's ticket: says why there is nowhere to write,
            rather than leaving a reader to wonder whether the field failed to
            render. */}
        {canWrite && ticket.status === 'claimed' && !canResolve && (
          <>
            <Separator $m="16px 0" />
            <Disclaimer $m="0" $center $color={lightTheme.lightGray}>
              Only {ticket.claimedBy ?? 'the person who claimed it'} can reply
              to or resolve this ticket. Hand it back to take it over.
            </Disclaimer>
          </>
        )}
      </Modal>

      <AttachOrderModal
        isOpen={attaching}
        ticket={ticket}
        busy={busy}
        onClose={() => setAttaching(false)}
        onAttach={attachOrder}
      />
    </>
  );
};
